import { useState } from 'react';
import type {
  AssessmentAuditResponse,
  DefinitionAuditResponse,
  EmployeeIdentityAuditResponse,
  ElevatedAttributeAccessAuditResponse,
} from '../../api/types';
import { describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import styles from './admin.module.css';

/**
 * The Audit Trail — admin-only UI over four already-implemented, already-
 * tested read APIs (`api/src/routes/audit.ts`: `/audit/assessment-activity`,
 * `/audit/definition-activity`, `/audit/employee-identity-activity`,
 * `/audit/elevated-attribute-access-activity`) that, until this section was
 * first built, had no frontend consumer at all — the Phase 2
 * reconnaissance's single largest chunk of backend-only surface. The fourth
 * trail (elevated-attribute access) was added in Mission 164, once that
 * event was persisted rather than merely logged to stdout.
 *
 * ---------------------------------------------------------------------
 * FOUR TRAILS, NOT ONE FEED
 *
 * Assessment activity, library/definition changes, identity provisioning
 * and elevated-attribute access each answer a different accountability
 * question and are stored in four separate tables (`api/src/routes/audit.ts`'s
 * own documentation explains why each is its own table rather than a
 * shared one). This section keeps that separation visible — a type
 * selector switches which trail is shown, rather than interleaving all
 * four into one merged, harder-to-audit timeline.
 *
 * This is a NEW UI over EXISTING data, not a new audit system: no new
 * route is added here, no new table, and no change to what any of the
 * four trails records or how.
 *
 * HR Partner does not receive this — `authorizeAuditAccess` is admin-only
 * and deliberately narrower than the general broad-read scope HR Partner
 * otherwise has (`api/src/authorization/scope.ts`'s own documentation);
 * this section lives on the same admin-only `/organisation` page as every
 * other admin-only control, so that boundary is inherited, not
 * re-implemented here.
 * ---------------------------------------------------------------------
 */
type AuditKind = 'assessment' | 'definition' | 'identity' | 'elevated';

export function AuditSection() {
  const [kind, setKind] = useState<AuditKind>('assessment');

  return (
    <section className={styles.section} aria-label="Audit trail">
      <h2 className={styles.sectionTitle}>Audit trail</h2>
      <p className={styles.sectionNote}>
        What has happened, who did it, and when — four separate trails, each answering a different question. Nothing
        here can be edited; this is a read-only record.
      </p>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="audit-kind">
          Trail
        </label>
        <select id="audit-kind" className={styles.input} value={kind} onChange={(e) => setKind(e.target.value as AuditKind)}>
          <option value="assessment">Assessment activity</option>
          <option value="definition">Library/definition changes</option>
          <option value="identity">Employee identity provisioning</option>
          <option value="elevated">Elevated attribute access</option>
        </select>
      </div>

      {kind === 'assessment' ? <AssessmentAuditList /> : null}
      {kind === 'definition' ? <DefinitionAuditList /> : null}
      {kind === 'identity' ? <IdentityAuditList /> : null}
      {kind === 'elevated' ? <ElevatedAttributeAccessAuditList /> : null}
    </section>
  );
}

function AssessmentAuditList() {
  const audit = useApiResource<AssessmentAuditResponse>('/audit/assessment-activity');
  if (audit.status === 'loading') return <LoadingState label="Loading assessment activity…" />;
  if (audit.status === 'failed') {
    return <ErrorState title="Assessment activity could not be loaded" detail={describeError(audit.error)} />;
  }
  if (audit.data.events.length === 0) {
    return <p className={styles.rowMeta}>No assessment activity has been recorded yet.</p>;
  }
  return (
    <ul className={styles.rowMeta}>
      {audit.data.events.map((event) => (
        <li key={event.id}>
          <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time> — {event.actor.fullName}{' '}
          <strong>{event.action}</strong> an assessment of <strong>{event.attribute.name}</strong> for {event.subject.fullName}
          {event.reason ? ` (${event.reason})` : ''}
        </li>
      ))}
    </ul>
  );
}

function DefinitionAuditList() {
  const audit = useApiResource<DefinitionAuditResponse>('/audit/definition-activity');
  if (audit.status === 'loading') return <LoadingState label="Loading definition activity…" />;
  if (audit.status === 'failed') {
    return <ErrorState title="Definition activity could not be loaded" detail={describeError(audit.error)} />;
  }
  if (audit.data.events.length === 0) {
    return <p className={styles.rowMeta}>No library/definition changes have been recorded yet.</p>;
  }
  return (
    <ul className={styles.rowMeta}>
      {audit.data.events.map((event) => (
        <li key={event.id}>
          <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time> — {event.actor.fullName}{' '}
          <strong>{event.action}</strong> {event.subjectType} "{event.subjectLabel}"
        </li>
      ))}
    </ul>
  );
}

function IdentityAuditList() {
  const audit = useApiResource<EmployeeIdentityAuditResponse>('/audit/employee-identity-activity');
  if (audit.status === 'loading') return <LoadingState label="Loading identity provisioning activity…" />;
  if (audit.status === 'failed') {
    return <ErrorState title="Identity provisioning activity could not be loaded" detail={describeError(audit.error)} />;
  }
  if (audit.data.events.length === 0) {
    return <p className={styles.rowMeta}>No identity provisioning activity has been recorded yet.</p>;
  }
  return (
    <ul className={styles.rowMeta}>
      {audit.data.events.map((event) => (
        <li key={event.id}>
          <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time> — {event.actor.fullName}{' '}
          <strong>{event.action}</strong> an identity link for {event.subject.fullName}
          {event.reason ? ` (${event.reason})` : ''}
        </li>
      ))}
    </ul>
  );
}

function ElevatedAttributeAccessAuditList() {
  const audit = useApiResource<ElevatedAttributeAccessAuditResponse>('/audit/elevated-attribute-access-activity');
  if (audit.status === 'loading') return <LoadingState label="Loading elevated attribute access activity…" />;
  if (audit.status === 'failed') {
    return <ErrorState title="Elevated attribute access activity could not be loaded" detail={describeError(audit.error)} />;
  }
  if (audit.data.events.length === 0) {
    return <p className={styles.rowMeta}>No elevated attribute access has been recorded yet.</p>;
  }
  return (
    <ul className={styles.rowMeta}>
      {audit.data.events.map((event) => (
        <li key={event.id}>
          <time dateTime={event.accessedAt}>{new Date(event.accessedAt).toLocaleString()}</time> — {event.accessor.fullName}{' '}
          read <strong>{event.attribute.name}</strong> for {event.target.fullName}
        </li>
      ))}
    </ul>
  );
}
